import {
  AccountData,
  FormCheckBoxField,
  FormInputType,
  FormRadioField,
  SavedFormQuestion,
  SavedFormResponse,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import filter from "lodash.filter";
import { Colors } from "../../../../../utils/colors";
import FlatList from "flatlist-react/lib";
import { CircularProgress } from "@mui/material";
import {
  addOrFilterRecipients,
  formQuestionDisplayName,
  handlePluralString,
  isFormResponsesOther,
  textableContactString,
  truncateString,
} from "@markit/common.utils";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { Icon } from "@iconify/react";
import CustomCheckbox from "../../../../CustomCheckbox";
import { API } from "../../../../../API";
import { SelectRecipientsSharedProps } from "../SelectRecipientsScreens";
import { EmptyStateFlatlist } from "../../../../EmptyStates/EmptyStateFlatlist";
import EmptyStateButton from "../../../../Buttons/EmptyStateButton";
import { SelectRecipientsDataItemResponses } from "./SelectRecipientsDataItemResponses";
import { useLoadUserList } from "../../../../../hooks/useLoadUserList";
import useAsyncOnMount from "../../../../../hooks/useAsyncEffectOnMount";
import ListTabs from "../../../../DropdownsAndTabs/ListTabs";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import SavedResponseProfileItem from "../../../../DisplayItem/DataQuestion/SavedResponseProfileItem";
import CustomLinkify from "../../../../Links/CustomLinkify";
import TextHoverButton from "../../../../Buttons/TextHoverButton";
import { downloadMediaFromFirestore } from "../../../../../utils/mediaUtils";
import DropdownArrowIcon from "../../../../DropdownsAndTabs/DropdownArrowIcon";

type SelectRecipientsCollectedDataProps = SelectRecipientsSharedProps & {
  selectedQuestion: SavedFormQuestion;
  selectable?: boolean;
};

const SelectRecipientsCollectedData = (
  props: SelectRecipientsCollectedDataProps
) => {
  const {
    unsavedRecipients,
    setUnsavedRecipients,
    excludingMode,
    setProfileSelected,
    selectedQuestion,
    selectable = true,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedFormOption, setSelectedFormOption] = useState("");
  const [formQuestionResponses, setFormQuestionResponses] = useState<
    Map<string, SavedFormResponse>
  >(new Map<string, SavedFormResponse>());
  const [optionStateValue, setOptionStateValue] = useState(0);
  const [showAllNumOptions, setShowAllNumOptions] = useState(false);

  const formQuestionResponsesArr = useMemo(
    () =>
      formQuestionResponses ? Array.from(formQuestionResponses.keys()) : [],
    [formQuestionResponses]
  );

  const optionsInputType = useMemo(
    () =>
      selectedQuestion.formInput.inputType === FormInputType.CHECK_BOX ||
      selectedQuestion.formInput.inputType === FormInputType.RADIO,
    [selectedQuestion.formInput.inputType]
  );

  const { listedActiveOptions, listedInactiveOptions, allOptions } =
    useMemo(() => {
      const input = selectedQuestion.formInput as
        | FormCheckBoxField
        | FormRadioField;
      const listedActiveOptions = input.options ?? [];
      const listedInactiveOptions = input.inactiveOptions ?? [];
      const hasOtherOption = Boolean(input.hasOtherOption);
      return {
        listedActiveOptions,
        listedInactiveOptions,
        allOptions: [
          ...listedActiveOptions,
          ...listedInactiveOptions,
          ...(hasOtherOption ? ["Other"] : []),
        ],
      };
    }, [selectedQuestion.formInput]);

  useAsyncOnMount(async () => {
    if (selectedQuestion && loading) {
      // this fetches the form responses of only attendees that are subscribed still
      const { formQuestionResponses } =
        await API.profile.fetchFollowersQuestionResponses({
          uid: accountData.uid,
          formQuestionId: selectedQuestion.id,
          allResponses: !selectable,
        });
      const formResponsesMap = new Map(formQuestionResponses);
      setFormQuestionResponses(formResponsesMap);
      setLoading(false);
    }
  });

  const {
    fetchedUserData,
    loadMoreUsers,
    isFinished,
    isLoading,
    loadSearchResultsThrottled,
  } = useLoadUserList({
    userIdList: formQuestionResponsesArr,
    windowSize: 30,
  });

  useEffect(() => {
    if (!loading) {
      loadMoreUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const containsUser = useCallback((userData: AccountData, query: string) => {
    return userData.fullName.toLowerCase().includes(query.toLowerCase());
  }, []);

  const containsOption = useCallback((option: string, query: string) => {
    return option.toLowerCase().includes(query.toLowerCase());
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
    loadSearchResultsThrottled(text.toLowerCase());
  };

  // The visible selected form question options to show (for checkbox and multiple choice case)
  const formQuestionOptionsToShow: string[] = useMemo(() => {
    let searchResults: any[] = [];
    if (
      selectedQuestion &&
      (selectedQuestion.formInput.inputType === FormInputType.CHECK_BOX ||
        selectedQuestion.formInput.inputType === FormInputType.RADIO)
    ) {
      const options = (
        optionStateValue === 0
          ? (selectedQuestion.formInput.options ?? [])
          : (selectedQuestion.formInput.inactiveOptions ?? [])
      ).concat(selectedQuestion.formInput.hasOtherOption ? ["Other"] : []);
      if (searchTerm !== "") {
        searchResults = filter(options, (option: string) => {
          return containsOption(option, searchTerm);
        });
      } else {
        searchResults = options;
      }
    }
    return searchResults;
  }, [containsOption, searchTerm, selectedQuestion, optionStateValue]);

  // The visible selected form question people to show (for non checkbox and multiple choice case)
  const formQuestionItemsToShow: AccountData[] = useMemo(() => {
    let searchResults = fetchedUserData;
    if (selectedQuestion) {
      if (searchTerm !== "") {
        searchResults = filter(searchResults, (user: AccountData) =>
          containsUser(user, searchTerm.toLowerCase())
        );
      }
    }
    return searchResults;
  }, [containsUser, fetchedUserData, searchTerm, selectedQuestion]);

  const iconColor = useMemo(
    () => (excludingMode ? Colors.RED3 : Colors.BLUE5),
    [excludingMode]
  );

  const isAllSelected = useCallback(
    (totalList: string[], subList: string[]) =>
      subList.length > 0
        ? totalList.every((sel) => subList.includes(sel))
        : false,
    []
  );

  const renderCheckbox = useCallback(
    (isChecked: boolean, onPress: () => void) => (
      <CustomCheckbox
        checked={isChecked}
        onChange={onPress}
        sx={{ padding: 0 }}
        altColor={iconColor}
      />
    ),
    [iconColor]
  );

  const renderEmptyView = useMemo(
    () => (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoading}
        containerStyles={{ paddingTop: 120 }}
        nonSearchEmptyView={
          <EmptyStateButton
            title={
              optionsInputType
                ? optionStateValue === 0
                  ? "No Active Response Options"
                  : "No Inactive Response Options"
                : "No Responses Collected"
            }
            icon={
              <Icon
                icon={optionsInputType ? "mdi:database" : "ion:person"}
                height={60}
              />
            }
            iconBox={70}
            containerStyles={{ paddingTop: 120 }}
          />
        }
      />
    ),
    [isLoading, optionStateValue, optionsInputType, searchTerm]
  );

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 14, paddingBottom: 14 }}>
        <div
          style={{
            backgroundColor: Colors.GRAY6,
            padding: 14,
            borderRadius: 12,
            gap: 14,
          }}
          className="ColumnNormal"
        >
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span style={{ fontSize: 14, fontWeight: "500" }}>
              {truncateString(
                selectedQuestion.formInput.label ?? "",
                190,
                "..."
              )}
            </span>
            <span style={{ color: Colors.GRAY1, fontSize: 12 }}>
              {formQuestionDisplayName(selectedQuestion.formInput)}
            </span>
          </div>
          {!loading ? (
            <>
              <HorizontalDivider altMargin={0} />
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="bodyMedium">
                  {formQuestionResponses.size}{" "}
                  {handlePluralString("Response", formQuestionResponses.size)}
                </span>
                {selectedQuestion.formInput.inputType ===
                  FormInputType.CHECK_BOX ||
                selectedQuestion.formInput.inputType === FormInputType.RADIO ? (
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    {allOptions
                      .slice(0, showAllNumOptions ? undefined : 5)
                      .map((option, index) => {
                        const subItemsToShow = new Map(
                          Array.from(formQuestionResponses.entries()).filter(
                            ([_, values]) =>
                              isFormResponsesOther(
                                option,
                                values.responses,
                                listedActiveOptions,
                                listedInactiveOptions
                              )
                          )
                        );
                        return (
                          <span key={index} className="smallBodySubtext">
                            {subItemsToShow.size} {option}
                          </span>
                        );
                      })}
                    {allOptions.length > 5 ? (
                      <TextHoverButton
                        text={
                          showAllNumOptions ? "Show Less..." : "Show All..."
                        }
                        onPress={() => setShowAllNumOptions(!showAllNumOptions)}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
        <SearchBoxContainer
          value={searchTerm}
          placeholder={"Search"}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          containerStyles={{ marginTop: 0 }}
        />
        {selectedQuestion.formInput.inputType === FormInputType.CHECK_BOX ||
        selectedQuestion.formInput.inputType === FormInputType.RADIO ? (
          <ListTabs
            tabLabels={["Active", "Inactive"]}
            tabNumbers={[-1, -1]}
            selectedValue={optionStateValue}
            onChange={setOptionStateValue}
            altColor={Colors.BLACK}
          />
        ) : null}
      </div>
      <div className="HideScrollbar AllowScroll" style={{ paddingBottom: 200 }}>
        {loading ? (
          <div className="Centering" style={{ paddingTop: 250 }}>
            <CircularProgress style={{ color: "#929292" }} size={20} />
          </div>
        ) : (
          <>
            {selectedQuestion.formInput.inputType === FormInputType.CHECK_BOX ||
            selectedQuestion.formInput.inputType === FormInputType.RADIO ? (
              <FlatList
                list={formQuestionOptionsToShow}
                renderWhenEmpty={() => renderEmptyView}
                renderItem={(item) => {
                  const subItemsToShow = new Map(
                    Array.from(formQuestionResponses.entries()).filter(
                      ([_, values]) =>
                        isFormResponsesOther(
                          item,
                          values.responses,
                          listedActiveOptions,
                          listedInactiveOptions
                        )
                    )
                  );
                  return (
                    <div>
                      <div className="AlignedRowSpaced" style={{ gap: 5 }}>
                        <div
                          onClick={() =>
                            setSelectedFormOption(
                              selectedFormOption !== item ? item : ""
                            )
                          }
                          className="AlignedRowSpacedSelect"
                          style={{
                            flexGrow: 1,
                            pointerEvents:
                              subItemsToShow.size > 0 ? "all" : "none",
                          }}
                        >
                          <div
                            className="ColumnNormalSelect"
                            style={{ gap: 3 }}
                          >
                            <span className="bodyMedium">{item}</span>
                            <span className="smallBodySubtext">
                              {selectable
                                ? textableContactString(subItemsToShow.size)
                                : `${subItemsToShow.size} ${handlePluralString(
                                    "Response",
                                    subItemsToShow.size
                                  )}`}
                            </span>
                          </div>
                          {subItemsToShow.size > 0 ? (
                            <DropdownArrowIcon
                              isOpen={selectedFormOption === item}
                              color={Colors.GRAY1}
                              height={18}
                              onClick={() =>
                                setSelectedFormOption(
                                  selectedFormOption !== item ? item : ""
                                )
                              }
                            />
                          ) : null}
                        </div>
                        {subItemsToShow.size > 0 && selectable
                          ? renderCheckbox(
                              isAllSelected(
                                Array.from(subItemsToShow.keys()),
                                unsavedRecipients
                              ),
                              () =>
                                addOrFilterRecipients(
                                  unsavedRecipients,
                                  setUnsavedRecipients,
                                  Array.from(subItemsToShow.keys())
                                )
                            )
                          : null}
                      </div>
                      <hr />
                      {selectedFormOption === item ? (
                        <SelectRecipientsDataItemResponses
                          responseUserIds={Array.from(subItemsToShow.keys())}
                          unsavedRecipients={unsavedRecipients}
                          setUnsavedRecipients={setUnsavedRecipients}
                          setProfileSelected={setProfileSelected}
                          excludingMode={excludingMode}
                          savedQuestion={selectedQuestion}
                          formResponses={formQuestionResponses}
                          selectable={selectable}
                        />
                      ) : null}
                    </div>
                  );
                }}
              />
            ) : (
              <FlatList
                list={formQuestionItemsToShow}
                renderItem={(item: AccountData) => {
                  const responseFound = formQuestionResponses.get(item.uid);
                  const response = responseFound
                    ? responseFound.responses.every(
                        (response) => response.trim() === ""
                      )
                      ? ["No Response Collected"]
                      : selectedQuestion.formInput.inputType ===
                          FormInputType.FILE_UPLOAD
                        ? ["View File"]
                        : responseFound.responses
                    : ["No Response Collected"];

                  return (
                    <div className="ColumnNormal">
                      <SavedResponseProfileItem
                        user={item}
                        savedQuestion={selectedQuestion}
                        formResponse={responseFound}
                        setProfileSelected={setProfileSelected}
                        unsavedRecipients={unsavedRecipients}
                        setUnsavedRecipients={setUnsavedRecipients}
                        excludingMode={excludingMode}
                        disableCheck={!selectable}
                        subtext={
                          selectedQuestion.formInput.inputType ===
                            FormInputType.FILE_UPLOAD &&
                          responseFound?.responses[0] ? (
                            <span
                              style={{
                                fontSize: 12,
                                color: Colors.BLUE5,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadMediaFromFirestore(
                                  [responseFound.responses[0]],
                                  `${item.fullName}-${selectedQuestion.id}`
                                );
                              }}
                            >
                              {response[0]}
                            </span>
                          ) : (
                            <CustomLinkify>
                              <span
                                className="TextOverflowEllipsis"
                                style={{ fontSize: 12, width: "360px" }}
                              >
                                {response.join(", ")}
                              </span>
                            </CustomLinkify>
                          )
                        }
                      />
                    </div>
                  );
                }}
                renderWhenEmpty={() => renderEmptyView}
                hasMoreItems={!isFinished}
                loadMoreItems={() => {
                  loadMoreUsers();
                }}
                paginationLoadingIndicator={() =>
                  isLoading && !isFinished ? (
                    <CircularProgress
                      style={{ color: "#929292", alignSelf: "center" }}
                      size={20}
                    />
                  ) : null
                }
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SelectRecipientsCollectedData;
